import useSWR from 'swr'
import React from 'react'

import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'

import CryptorTag from '../components/CryptorTag'
import CVTest from '../components/CVTest'
import Announce from '../components/Announce'
import Reports from '../components/Reports'
import CVList from '../components/CVList'
import BigSpinning from '../containers/BigSpinning'

import CampaignsContainer from './CampaignsContainer'
import ItemsContainer from './ItemsContainer'
import PageGroupsContainer from './PageGroupsContainer'
import ShopsContainer from './ShopsContainer'
import Information from '../components/Information'
import ReportMailsContainer from './ReportMailsContainer'
import { FETCH_KEYS, fetchCurrentUser, fetcherWithAuth, readCookie } from '../api'
import InviterListContainer from './InviterListContainer'
import CrmScoringContainer from './CrmScoringContainer'
import GuestListContainer from './GuestListContainer'
import BlacklistContainer from './BlacklistContainer'
import EmailNoticeContainer from './EmailNoticeContainer'
import EnduserEventsContainer from './EnduserEventsContainer'
import InitialMessageContainer from './InitialMessageContainer'
import CouponContentContainer from './CouponContentContainer'
import CouponList from '../components/CouponList'
import ComingCouponEmailNoticeContainer from './ComingCouponEmailNoticeContainer'
import UsersContainer from './UsersContainer'
import { ADMIN_TYPE } from '../../models/user'
import Client from '../../models/client'
import { Box, Menu, NavLink, ScrollArea, Text } from '@mantine/core'
import { BellIcon, ChartLineIcon, CheckCircleIcon, DotsThreeIcon, FilesIcon, FlagIcon, GearIcon, HouseIcon, MegaphoneIcon, SquaresFourIcon, StorefrontIcon, TicketIcon, UserCircleIcon, WrenchIcon } from '@phosphor-icons/react'
import IncentiveListSavedPage from '../components/IncentiveListSavedPage'
import CVTagContainer from './CVTagContainer'
import InquiryContainer from './InquiryContainer'
import ComingConfContainer from './ComingConfContainer'

const SIDEBAR_WIDTH = 235

const styles = {
  listPaper: {
    flex: `0 0 ${SIDEBAR_WIDTH}px`,
    minWidth: SIDEBAR_WIDTH,
    minHeight: 600,
    flexDirection: 'column' as 'column',
    display: 'flex',
    height: 'calc(100vh - 68px)',
    padding: 8
  },
  section: {
    display: 'flex',
  },
  menuLink: {
    textDecoration: 'none',
  },
  child: {
    width: '100%',
    height: 'calc(100vh - 68px)',
    backgroundColor: "#fff",
    borderRadius: "16px 0 0 0"
  },
  nested: {
    paddingLeft: '4em',
  },
}

const ClientContainer: React.FC = () => {
  const { urlName } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const clientId = Number(readCookie('last_client_id'))
  const { data, isLoading, error } = useSWR<{ client: Client }>(
    FETCH_KEYS.fetchClient(clientId),
    fetcherWithAuth,
  )

  const { data: currentUser } = useSWR(
    data ? `/admin/clients/${data.client.id}/users/current` : null,
    () => fetchCurrentUser(data!.client.id),
  )

  if (!data || !currentUser) return <BigSpinning />

  const { client } = data
  const invyServiceUse = client.invy_service_use
  const pageCreation = client.page_creation
  const isAdmin = currentUser.admin_type === ADMIN_TYPE.ADMIN

  // --- サイドバー ---
  const sidebar = () => {
    const navContents = [
      {
        icon: HouseIcon,
        label: "トップページ",
        href: `/admin/clients/${urlName}`,
        category: 1,
        admin: false
      },
      {
        icon: FlagIcon,
        label: "CV管理",
        category: 1,
        admin: false,
        nest: [
          {
            label: "CV一覧",
            href: `/admin/clients/${urlName}/cv-list`,
            category: 1,
          },
          {
            label: "特典付与リスト",
            href: `/admin/clients/${urlName}/incentive-lists-saved`,
            category: 1,
          },
        ]
      },
      {
        icon: ChartLineIcon,
        label: "紹介データ分析",
        category: 1,
        admin: false,
        nest: [
          {
            label: "サマリー",
            href: `/admin/clients/${urlName}/reports`,
            category: 1,
          },
          {
            label: "紹介者一覧",
            href: `/admin/clients/${urlName}/inviter-list`,
            category: 1,
          },
          {
            label: "ゲスト一覧",
            href: `/admin/clients/${urlName}/guest-list`,
            category: 1,
          },
        ]
      },
      {
        icon: SquaresFourIcon,
        label: "CRM",
        category: 1,
        admin: false,
        nest: [
          {
            label: "CRMスコアリング",
            href: `/admin/clients/${urlName}/crm-scoring/influence`,
            activePrefix: `/admin/clients/${urlName}/crm-scoring`,
            category: 1,
          },
        ]
      },
      {
        icon: StorefrontIcon,
        label: "店舗管理",
        href: `/admin/clients/${urlName}/shops`,
        category: 2,
      },
      {
        icon: MegaphoneIcon,
        label: "告知設定",
        href: `/admin/clients/${urlName}/announce`,
        category: 2,
      },
      {
        icon: BellIcon,
        label: "紹介者CV通知",
        category: 2,
        nest: [
          {
            label: "イベント設定",
            href: `/admin/clients/${urlName}/enduser-events`,
            category: 2,
          },
          {
            label: "メール通知設定",
            href: `/admin/clients/${urlName}/notices`,
            category: 2,
          },
        ],
      },
      ...((invyServiceUse == '1' || invyServiceUse == '2') ? [{
        icon: TicketIcon,
        label: "来店クーポン管理",
        category: 2,
        nest: [
          {
            label: "メール通知設定",
            href: `/admin/clients/${urlName}/coming-coupon-cv-notices`,
            category: 2,
          },
          {
            label: "クーポン画面設定",
            href: `/admin/clients/${urlName}/coupon_contents`,
            category: 2,
          },
          {
            label: "クーポン利用管理",
            href: `/admin/clients/${urlName}/coupon-reports`,
            category: 2,
          },
        ]
      }] : []),
      {
        icon: GearIcon,
        label: "オプション",
        category: 2,
        nest: [
          {
            label: "ブラックリスト",
            href: `/admin/clients/${urlName}/blacklist`,
            category: 2,
          },
          {
            label: "CV通知",
            href: `/admin/clients/${urlName}/report-mails`,
            category: 2,
          },
        ]
      },
      {
        icon: FilesIcon,
        label: "ページ一覧",
        href: `/admin/clients/${urlName}/page-groups`,
        category: 3,
        flag: "htmls"
      },
      {
        icon: FilesIcon,
        label: "ページ一覧",
        href: `/admin/clients/${urlName}/campaigns`,
        category: 3,
        flag: "light"
      },
      {
        icon: WrenchIcon,
        label: "紹介ページ設定",
        category: 3,
        nest: [
          {
            label: "ファイルアップ",
            href: `/admin/clients/${urlName}/items`,
            category: 3,
          },
          {
            label: "紹介メッセージ設定",
            href: `/admin/clients/${urlName}/initial-message`,
            category: 3,
          },
          ...((invyServiceUse == '0' || invyServiceUse == '2') ? [{
            label: "計測タグ設定",
            href: `/admin/clients/${urlName}/cv-tag`,
            category: 3,
          }] : []),
          ...((invyServiceUse == '1' || invyServiceUse == '2') ? [{
            label: "来店計測設定",
            href: `/admin/clients/${urlName}/coming-conf`,
            category: 3,
          }] : []),
        ]
      },
      {
        icon: CheckCircleIcon,
        label: "テスト検証",
        href: `/admin/clients/${urlName}/cv-test`,
        category: 3,
      },
    ];

    let filteredByPage
    if (pageCreation == '0') filteredByPage = navContents.filter(item => item.flag !== "htmls")
    else filteredByPage = navContents.filter(item => item.flag !== "light")
    const navItem_notAdmin = filteredByPage.filter(item => item.admin === false)

    return createNavItem(isAdmin ? filteredByPage : navItem_notAdmin)
  }

  const createNavItem = (navEl: any) => {
    const navItem = navEl.map((item: any) => {
      if (item.nest) {
        let color = "";
        const currentPath = location.pathname.replace(/\/$/, "");

        // activePrefix 指定時は配下のパスもアクティブ扱いにする（画面内タブでURLが変わる画面向け）
        const isSubItemActive = (subItem: any) => {
          if (!subItem.activePrefix) return currentPath === subItem.href;
          return currentPath === subItem.activePrefix || currentPath.startsWith(`${subItem.activePrefix}/`);
        };
        const isAnyChildActive = item.nest.some(isSubItemActive);

        const navItem_nest = item.nest.map((subItem: any) => {
          const isActive = isSubItemActive(subItem);
          switch (subItem.category) {
            case 1: color = "#DE5B5B"; break;
            case 2: color = "#4BC1C7"; break;
            case 3: color = "#5DA327"; break;
          }

          return (
            <NavLink
              onClick={() => navigate(subItem.href)}
              key={subItem.label}
              active={isActive}
              label={subItem.label}
              variant="filled"
              color={color}
              c={isActive ? "#fff" : "#00000099"}
              fw={400}
              fz={13}
              bdrs={8}
              styles={{
                root: {
                  '--nl-hover': isActive ? color : 'transparent',
                  '--mantine-color-gray-0': '#00000008'
                },
              }}
            />
          )
        });

        return (
          <NavLink
            key={item.label}
            label={item.label}
            leftSection={<item.icon width={20} height={20} weight="fill" color={color} />}
            defaultOpened={isAnyChildActive}
            fw={600}
            fz={14}
            p={8}
            styles={{
              root: {
                '--nl-hover': 'transparent',
                '--mantine-color-gray-0': '#00000008'
              },
            }}
          >
            {navItem_nest}
          </NavLink>
        )
      } else {
        const isActive = location.pathname.replace(/\/$/, "") === item.href;

        let color: string = "";
        switch (item.category) {
          case 1: color = "#DE5B5B"; break;
          case 2: color = "#4BC1C7"; break;
          case 3: color = "#5DA327"; break;
        }

        return (
          <NavLink
            onClick={() => navigate(item.href)}
            key={item.label}
            active={isActive}
            label={item.label}
            leftSection={<item.icon width={20} height={20} weight="fill" color={isActive ? undefined : color} />}
            variant="filled"
            color={color}
            fw={600}
            fz={14}
            bdrs={8}
            p={8}
            styles={{
              root: {
                '--nl-hover': isActive ? color : 'transparent',
                '--mantine-color-gray-0': '#00000008'
              },
            }}
          />
        )
      }
    })

    return (<Box>{navItem}</Box>)
  }
  // --- サイドバー ---

  const renderMenu = () => {
    const currentPath = location.pathname.replace(/\/$/, "")
    const usersPath = `/admin/clients/${urlName}/users`
    const inquiryPath = `/admin/clients/${urlName}/inquiry`
    const usersActive = currentPath === usersPath
    const inquiryActive = currentPath === inquiryPath

    return (
      <Box style={styles.listPaper} bg="#F7E6EB">
        <Text
          fz={12}
          c="#00000099"
          m={8}
        >
          {client.name} 様
        </Text>

        <ScrollArea my={4}>
          {sidebar()}
        </ScrollArea>

        <Menu position="right-end" offset={4} width={200} shadow="md">
          <Menu.Target>
            <NavLink
              component="button"
              label="アカウント設定"
              leftSection={<UserCircleIcon width={20} height={20} weight="fill" color="#CA8E00" />}
              rightSection={<DotsThreeIcon size={20} weight="bold" />}
              fw={600}
              fz={14}
              bdrs={8}
              p={8}
              mt="auto"
              styles={{
                root: {
                  '--nl-hover': 'transparent',
                  '--mantine-color-gray-0': '#00000008'
                },
              }}
            />
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              onClick={() => navigate(usersPath)}
              fw={usersActive ? 600 : 400}
              c={usersActive ? '#CA8E00' : undefined}
            >
              ID一覧
            </Menu.Item>
            <Menu.Item
              onClick={() => navigate(inquiryPath)}
              fw={inquiryActive ? 600 : 400}
              c={inquiryActive ? '#CA8E00' : undefined}
            >
              お問い合わせ
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
    )
  }

  return (
    <div>
      {!client ? (
        <BigSpinning />
      ) : (
        <div>
          <section style={styles.section}>
            {renderMenu()}
            <div style={{ backgroundColor: "#F7E6EB", width: "100%" }}>
              <ScrollArea style={styles.child}>
                <div style={{ padding: "40px 40px 110px", maxWidth: `calc(100vw - ${SIDEBAR_WIDTH}px)` }}>
                  <Routes>
                    <Route
                      path={`/info`}
                      element={<Information />}
                    />
                    <Route
                      path={`/reports`}
                      element={<Reports client={client} />}
                    />
                    <Route
                      path={`/inviter-list`}
                      element={<InviterListContainer client={client} />}
                    />
                    <Route
                      path={`/guest-list`}
                      element={<GuestListContainer client={client} />}
                    />
                    <Route
                      path={`/cv-list`}
                      element={<CVList client={client} />}
                    />
                    <Route
                      path={`/incentive-lists-saved`}
                      element={<IncentiveListSavedPage client={client} />}
                    />
                    <Route
                      path={`/crm-scoring/influence`}
                      element={<CrmScoringContainer client={client} axis="f2_m2" />}
                    />
                    <Route
                      path={`/crm-scoring/active`}
                      element={<CrmScoringContainer client={client} axis="r2_f2" />}
                    />
                    {/* 管理者ガードを追加しつつ順番を維持 */}
                    {currentUser.admin_type === ADMIN_TYPE.ADMIN && (
                      <>
                        <Route
                          path={`/cv-tag`}
                          element={<CVTagContainer client={client} />}
                        />
                        <Route
                          path={`/coming-conf`}
                          element={<ComingConfContainer client={client} />}
                        />
                        <Route
                          path={`/cv-test`}
                          element={<CVTest client={client} />}
                        />
                        <Route
                          path={`/announce`}
                          element={<Announce client={client} />}
                        />
                        <Route
                          path={`/shops`}
                          element={<ShopsContainer client={client} />}
                        />
                        <Route path={`/cryptor-tag`} element={<CryptorTag />} />
                        <Route
                          path={`/page-groups`}
                          element={<PageGroupsContainer client={client} />}
                        />
                        <Route
                          path={`/campaigns`}
                          element={<CampaignsContainer client={client} />}
                        />
                        <Route
                          path={`/items`}
                          element={<ItemsContainer client={client} />}
                        />
                        <Route
                          path={`/initial-message`}
                          element={<InitialMessageContainer client={client} />}
                        />
                        <Route
                          path={`/report-mails`}
                          element={<ReportMailsContainer client={client} />}
                        />
                      </>
                    )}
                    {/* 管理者ガード */}
                    {currentUser.admin_type === ADMIN_TYPE.ADMIN && (
                      <>
                        <Route
                          path={`/blacklist`}
                          element={<BlacklistContainer client={client} />}
                        />
                        <Route
                          path={`/notices`}
                          element={<EmailNoticeContainer client={client} />}
                        />

                        <Route
                          path={`/coming-coupon-cv-notices`}
                          element={<ComingCouponEmailNoticeContainer client={client} />}
                        />

                        <Route
                          path={`/enduser-events`}
                          element={<EnduserEventsContainer client={client} />}
                        />

                        {(invyServiceUse == '1' || invyServiceUse == '2') && (
                          <>
                            <Route
                              path={`/coupon_contents`}
                              element={<CouponContentContainer client={client} />}
                            />
                            <Route
                              path={`/coupon-reports`}
                              element={<CouponList client={client} />}
                            />
                          </>
                        )}
                      </>
                    )}

                    <Route
                      path={`/users`}
                      element={<UsersContainer client={client} />}
                    />

                    <Route
                      path={`/inquiry`}
                      element={<InquiryContainer />}
                    />

                    <Route path={`/`} element={<Information />} />
                  </Routes>
                </div>
              </ScrollArea>
            </div>
          </section>
        </div>
      )
      }
    </div >
  )
}

export default ClientContainer
